import React from 'react';
import { Link } from 'react-router-dom';
import LearnMore from '../LearnMore';
import { track, EVENTS } from '../../services/analytics';
import { cost } from '../../data/patentLabEvidence';
import { BUILDER_PACK_STEPS, isFirstStep, ANY_ORDER_LABEL, SPINE_SHORTS, SAMPLE } from '../../data/builderPack';
import { landingHeading } from '../../content/buildNames';
import fig1 from '../../assets/patent-lab-sample/fig-1.svg';

// What Build actually hands a student, on the landing page. Founder
// 2026-09-23: "in the landing page, lets quote the attorney assisted
// provisional patent application ... that would itself cost in the thousands
// ... put some of the visuals from this page". Founder 2026-09-26, pick
// Landing A: the band grows one section wider, the four-step arc in place of
// the four-sheet grid, one real artifact per step (the interview card, FIG. 1
// from the sample application, a review item, the presenter view), and the
// "why it counts" box gains the sourced research and journal facts. The price
// aside and its button stay exactly where they were.
//
// FIG. 1 is a real run: the test invention walked end to end on production on
// 2026-09-23 (a patient-transport tag), drawn by the lab's own assembler. The
// answers and the review item are sample text, said so on the page. Since
// 2026-09-28 (#2396) Brainstorm is the arc's one numbered step: the three
// after it keep their artifact each but sit unnumbered under "Then, in any
// order", because after the brainstorm there is no order. The price
// is the sourced tile from data/patentLabEvidence.js (tag S4), so the drift
// test that pins every number to the research record covers it here too. The
// research and journal lines quote facts the resource papers already carry
// with their sources (patent-pending-on-your-application,
// your-own-research-on-your-application); nothing here is new.
const ATTORNEY = cost.tiles.find((t) => t.tag === 'S4');
const USPTO_FEE = cost.tiles.find((t) => t.tag === 'S1');

const CAPTIONS = {
    brainstorm: 'The seven answers',
    patent: 'FIG. 1 of the specification',
    research: 'One review item',
    pitch: 'The presenter view',
};

const MINI = 'absolute inset-0 overflow-hidden bg-white p-2 text-[8px] leading-[1.35] text-[#2C2C2C] sm:text-[9px]';
const ROW = 'mt-1.5 text-[7px] font-bold uppercase tracking-wider text-[#6B6B6B]';
const PILL = 'mt-1.5 inline-block rounded-full border border-[#9A7B2F] px-1.5 text-[7px] font-bold uppercase tracking-wide text-[#9A7B2F]';

function InterviewCard() {
    return (
        <div className={MINI} data-testid="builder-pack-artifact-brainstorm">
            <p className="m-0 mb-1 font-serif text-[10px] font-semibold">Your interview</p>
            {SPINE_SHORTS.map((short, i) => (
                <React.Fragment key={short}>
                    <div className={`mt-[3px] flex gap-1 ${i === 0 ? 'text-[#2C2C2C]' : 'text-[#4A4A4A]'}`}>
                        <span className="w-2 shrink-0 font-bold text-[#9A7B2F]">{i + 1}</span>
                        <span>{short.charAt(0).toUpperCase() + short.slice(1)}</span>
                    </div>
                    {i === 0 ? (
                        <div className="ml-3 mt-0.5 rounded-sm border-l-2 border-[#1A4D2E] bg-[#FBF8F0] px-1 py-0.5 text-[7.5px]">"{SAMPLE.answer}"</div>
                    ) : null}
                </React.Fragment>
            ))}
            <span className={PILL}>saved as typed</span>
        </div>
    );
}

function SheetCard() {
    return (
        <img src={fig1} alt="FIG. 1 from the sample application" loading="lazy"
             className="absolute inset-0 h-full w-full object-contain p-2" data-testid="builder-pack-artifact-patent" />
    );
}

function ReviewCard() {
    const r = SAMPLE.review;
    return (
        <div className={MINI} data-testid="builder-pack-artifact-research">
            <p className="m-0 mb-1 font-serif text-[10px] font-semibold">{r.title}</p>
            <p className={`${ROW} mt-0`}>{r.row}</p>
            <p className="m-0 mt-0.5 border-l-2 border-[#9A7B2F] bg-[#FDFCF7] px-1 py-0.5 italic">"{r.quote}"</p>
            <p className={ROW}>What the mentor found</p>
            <div>{r.found}</div>
            <p className={ROW}>Its question</p>
            <div>{r.question}</div>
            <span className={PILL}>required</span>
        </div>
    );
}

function PresenterCard() {
    const s = SAMPLE.slide;
    return (
        <div className={MINI} data-testid="builder-pack-artifact-pitch">
            <div className="flex aspect-video flex-col justify-center overflow-hidden rounded-[3px] bg-[#1A4D2E] p-1.5 text-white">
                <div className="font-serif text-[8px] font-semibold leading-tight">{s.line}</div>
                <div className="mt-1 text-[6px] opacity-85">{s.from}</div>
            </div>
            <div className="mt-1.5 text-[7.5px] text-[#4A4A4A]">{s.script}</div>
            <div className="mt-1 flex justify-between border-t border-[#E0DED8] pt-0.5 font-mono text-[6.5px] text-[#6B6B6B]">
                {s.timer.map((t) => <span key={t}>{t}</span>)}
            </div>
        </div>
    );
}

const ARTIFACTS = { brainstorm: InterviewCard, patent: SheetCard, research: ReviewCard, pitch: PresenterCard };

// One step of the arc: its artifact, caption, name and record. Only the
// first step carries a number (#2396).
function ArcStep({ s, number = null }) {
    const Artifact = ARTIFACTS[s.key];
    return (
        <li className="min-w-0" data-testid={`builder-pack-arc-${s.key}`}>
            <div data-replica className="relative aspect-[3/4] overflow-hidden rounded-lg border border-[#E0DED8] bg-white">
                <Artifact />
            </div>
            <p className="mt-1 text-center text-xs font-semibold text-[#6B6B6B]">{CAPTIONS[s.key]}</p>
            <p className="mt-2 text-sm font-semibold leading-tight text-[#2C2C2C]">
                {number ? <span className="lining-nums tabular-nums text-[#8A6B1F]" data-testid="patent-lab-proof-number">{number} </span> : null}{s.title}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-[#4A4A4A]">{s.line}</p>
            <p className="mt-1.5 border-l-2 border-[#9A7B2F] pl-2 text-xs leading-snug text-[#2C2C2C]">{s.record}</p>
        </li>
    );
}

export default function PatentLabProof() {
    const steps = BUILDER_PACK_STEPS;
    const first = steps.find(isFirstStep);
    const peers = steps.filter((s) => !isFirstStep(s));
    return (
        <section className="bg-transparent" data-testid="patent-lab-proof" data-steps={steps.length}>
            <div className="max-w-6xl mx-auto">
                <p className="text-xs font-bold uppercase tracking-wider text-[#8A6B1F]">What your student ends up with</p>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-bold text-[#1A4D2E] max-w-3xl" style={{ textWrap: 'balance' }}>
                    {landingHeading(steps.map((s) => s.key))}
                </h3>

                <div className="mt-8 grid lg:grid-cols-[1fr_340px] gap-6 items-start">
                    <div className="min-w-0">
                        <ol className="m-0 grid list-none grid-cols-2 gap-3.5 p-0 sm:grid-cols-4" data-testid="builder-pack-arc">
                            <ArcStep s={first} number={1} />
                            <li className="col-span-2 min-w-0 sm:col-span-3">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[#8A6B1F]" id="builder-pack-arc-peers-label" data-testid="patent-lab-proof-any-order">{ANY_ORDER_LABEL}</p>
                                <ul role="list" aria-labelledby="builder-pack-arc-peers-label" className={`m-0 grid list-none grid-cols-2 gap-3.5 p-0 ${peers.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                                    {peers.map((s) => <ArcStep key={s.key} s={s} />)}
                                </ul>
                            </li>
                        </ol>
                        <p className="mt-3 text-xs text-[#6B6B6B] max-w-prose">
                            A sample: a patient-transport tag, a test invention run through the patent steps this week. The answers and
                            the review item shown are sample text; every feature in the specification traces to an answer the
                            inventor gave; nothing was invented to fill a gap.
                        </p>
                        {/* Founder 2026-09-23: "put some of this in the landing page"
                            (why a filed provisional counts on an application). Said the
                            honest way: filed and pending, never "patented". Every fact
                            names its source on the line. */}
                        <div className="mt-6 rounded-xl border border-[#E0DED8] bg-[#FDFCF7] p-5" data-testid="patent-lab-proof-why">
                            <p className="text-sm font-semibold text-[#1A4D2E]">Why it counts on a college application</p>
                            <ul className="mt-3 space-y-2 text-sm text-[#2C2C2C] max-w-prose">
                                <li>
                                    <span className="font-semibold">The most selective colleges rate what you did outside class.</span>{' '}
                                    In Harvard's reading procedures, entered into the court record, a 1 is "national-level, professional or
                                    other truly unusual achievement". 24% of applicants were rated 1 or 2 on extracurriculars, against 42% on
                                    academics. Strong grades are common; something checkable is not.
                                    <span className="mt-0.5 block text-xs text-[#6B6B6B]">The federal court record, via Patent Pending on a College Application</span>
                                </li>
                                <li>
                                    <span className="font-semibold">The filing has a number and a date.</span>{' '}
                                    Listed as "filed a U.S. provisional patent application", patent pending, it stands through every deadline;
                                    the twelve months to convert run past decision day, so the family decides then.
                                    <span className="mt-0.5 block text-xs text-[#6B6B6B]">USPTO; the resources paper</span>
                                </li>
                                <li data-testid="patent-lab-proof-why-research">
                                    <span className="font-semibold">A paper alone does not carry; the account of who did what does.</span>{' '}
                                    Admissions readers told ProPublica in May 2023 they "can't tell if a paper in the sciences means
                                    anything", and MIT's dean said the office weighs "the mentor's recommendation". The paper step's
                                    log is that account, in the five lines Regeneron STS asks for.
                                    <span className="mt-0.5 block text-xs text-[#6B6B6B]">ProPublica, May 18, 2023; MIT Admissions; STS 2027 Project Recommendation</span>
                                </li>
                                <li data-testid="patent-lab-proof-why-journals">
                                    <span className="font-semibold">The journals set the bar, and the paper step is built to it.</span>{' '}
                                    JEI charges $49 per submission and requires an adult senior mentor as the last author; ISEF requires
                                    the plan and its approvals before experimentation starts. The plan is locked before the data step opens.
                                    <span className="mt-0.5 block text-xs text-[#6B6B6B]">JEI submission pages; ISEF 2026-27 rules</span>
                                </li>
                                <li>
                                    <span className="font-semibold">The number is the proof; the story is what readers score.</span>{' '}
                                    The pack starts from your student's own record, so the invention, the activities list and the essay
                                    tell one story.
                                </li>
                            </ul>
                            <div className="mt-3" onClickCapture={() => track(EVENTS.PATENT_LAB_PROOF_CLICKED, { cta: 'paper' })}>
                                <LearnMore slug="patent-pending-on-your-application" label="What a provisional application says on a college application" />
                            </div>
                        </div>
                    </div>

                    <aside className="rounded-2xl border border-[#CFE3D4] bg-[#F5F8F5] p-6" data-testid="patent-lab-proof-price">
                        <p className="font-serif text-3xl font-bold text-[#1A4D2E] lining-nums tabular-nums">{ATTORNEY.n}</p>
                        <p className="mt-2 text-sm text-[#2C2C2C] leading-snug">{ATTORNEY.line}.</p>
                        <p className="mt-1 text-xs text-[#6B6B6B]">{ATTORNEY.source}</p>
                        <p className="mt-5 text-base font-semibold text-[#2C2C2C]">
                            With Stratia, all of it is part of every Season Pass, and one application is included.
                        </p>
                        <p className="mt-1 text-sm text-[#4A4A4A]">
                            Your student writes it here, in their own words, and you file it yourselves with the USPTO. The USPTO's
                            own fee, {USPTO_FEE.n} by entity size, is paid to the USPTO.
                        </p>
                        <Link to="/pricing" className="mt-5 inline-flex h-12 items-center rounded-xl bg-[#1A4D2E] px-6 text-base font-semibold text-white hover:bg-[#2D6B45]"
                              data-testid="patent-lab-proof-cta"
                              onClick={() => track(EVENTS.PATENT_LAB_PROOF_CLICKED, { cta: 'season_pass' })}>
                            See the Season Pass
                        </Link>
                    </aside>
                </div>
            </div>
        </section>
    );
}

// The four real artifact minis and their captions, shared with the landing
// page's Build cards (components/landing/home/BuildTools.jsx) so both show
// the same pictures from one definition.
export { InterviewCard, SheetCard, ReviewCard, PresenterCard, CAPTIONS as BUILD_ARTIFACT_CAPTIONS };
